import { useMemo, useState } from 'react';

import { appliancesOperations } from '../undoStack.constants';

import { UserDesignOperation } from '~/core/enums';
import { DrawingShape } from '~/core/models';
import { useAppSelector } from '~/redux/hooks';
import {
  selectLastUserSelectedDesignOperation,
  selectUserDesignOperationsData,
} from '~/redux/slices/undoStack';

export const useUndoStack = () => {
  const [shapesStack, setShapesStack] = useState<DrawingShape[][]>([]);

  const {
    latestPushedOperation: latestPushedUserDesignOperation,
    latestBeforeUndoRedo: latestOperationBeforeUndoRedo,
    selectedOperations,
  } = useAppSelector(selectUserDesignOperationsData);
  const lastUserSelectedDesignOperation = useAppSelector(
    selectLastUserSelectedDesignOperation
  );

  const isLatestOperationAnUndoRedoAction = useMemo(
    () =>
      lastUserSelectedDesignOperation?.startsWith(UserDesignOperation.UNDO) ||
      lastUserSelectedDesignOperation?.startsWith(UserDesignOperation.REDO),
    [lastUserSelectedDesignOperation]
  );

  const isAnApplianceOperation = (operation: string) =>
    appliancesOperations.some((op) => operation.startsWith(op));

  const isAddingAnApplianceTheLastOperationBeforeUndoRedo = useMemo(
    () =>
      !!latestOperationBeforeUndoRedo &&
      isAnApplianceOperation(latestOperationBeforeUndoRedo),
    [latestOperationBeforeUndoRedo]
  );

  const secondToLatestOperationWasAddingAppliance = useMemo(() => {
    if (!latestOperationBeforeUndoRedo) {
      return false;
    }
    const latestOperationIndex = selectedOperations.findIndex(
      (op) => op === latestOperationBeforeUndoRedo
    );
    const latestOperation = selectedOperations[latestOperationIndex];

    if (
      !isAnApplianceOperation(latestOperation) ||
      latestOperationIndex === 0
    ) {
      return false;
    }

    const secondToLatestOperation =
      selectedOperations[latestOperationIndex - 1];

    return isAnApplianceOperation(secondToLatestOperation);
  }, [latestOperationBeforeUndoRedo, selectedOperations]);

  return {
    shapesStack,
    setShapesStack,
    lastUserSelectedDesignOperation,
    isLatestOperationAnUndoRedoAction,
    isAddingAnApplianceTheLastOperationBeforeUndoRedo,
    latestPushedUserDesignOperation,
    secondToLatestOperationWasAddingAppliance,
  };
};
